/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { highlightPath, highlightPathActive, resetPathLinks } from '../../../helpers/graph';
import { NODE_COMMON_STYLES, NODE_STYLES } from '../graph-config';
import { createNodeToolTip, GoMake } from './shared';

/**
 * Agent node template (circle - blue) with icon and label below
 */
export function createAgentNodeTemplate($: GoMake): go.Node {
  return $(
    go.Node,
    'Vertical',
    {
      selectionAdorned: false,
      layoutConditions: go.LayoutConditions.Added | go.LayoutConditions.Removed,
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const circle = n.findObject('AGENT_CIRCLE') as go.Shape;
        if (n.isSelected) {
          // Active state
          if (circle) {
            circle.stroke = NODE_STYLES.agent.selected.stroke;
            circle.strokeWidth = NODE_STYLES.agent.selected.strokeWidth;
          }
          n.isShadowed = true;
          n.shadowColor = NODE_STYLES.agent.selected.shadowColor;
          n.shadowBlur = NODE_STYLES.agent.selected.shadowBlur;
          n.shadowOffset = new go.Point(NODE_STYLES.agent.selected.shadowOffset.x, NODE_STYLES.agent.selected.shadowOffset.y);
          // Highlight path links in active state
          highlightPathActive(n);
        } else {
          // Reset to default state — respect error status
          const hasError = n.data?.hasError;
          if (circle) {
            circle.stroke = hasError ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.agent.stroke;
            circle.strokeWidth = hasError ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.agent.strokeWidth;
          }
          n.isShadowed = false;
          // Reset path links to default
          resetPathLinks(n);
        }
      },
      mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        highlightPath(node, null, true, true);
        // Don't override active (selected) styling
        if (node.isSelected) return;
        const circle = node.findObject('AGENT_CIRCLE') as go.Shape;
        if (circle) {
          circle.stroke = NODE_STYLES.agent.hover.stroke;
          circle.strokeWidth = NODE_STYLES.agent.hover.strokeWidth;
        }
        node.isShadowed = true;
        node.shadowColor = NODE_STYLES.agent.hover.shadowColor;
        node.shadowBlur = NODE_STYLES.agent.hover.shadowBlur;
        node.shadowOffset = new go.Point(NODE_STYLES.agent.hover.shadowOffset.x, NODE_STYLES.agent.hover.shadowOffset.y);
      },
      mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        highlightPath(node, null, false, true);
        // Don't reset if node is selected (keep active styling)
        if (node.isSelected) return;
        const circle = node.findObject('AGENT_CIRCLE') as go.Shape;
        const hasError = node.data?.hasError;
        if (circle) {
          circle.stroke = hasError ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.agent.stroke;
          circle.strokeWidth = hasError ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.agent.strokeWidth;
        }
        node.isShadowed = false;
      },
      toolTip: createNodeToolTip($)
    },
    // Circle with icon inside
    $(
      go.Panel,
      'Spot',
      // Circle shape as background
      $(go.Shape, NODE_STYLES.agent.shape, {
        name: 'AGENT_CIRCLE',
        desiredSize: NODE_STYLES.agent.size,
        fill: NODE_STYLES.agent.fill,
        stroke: NODE_STYLES.agent.stroke,
        strokeWidth: NODE_STYLES.agent.strokeWidth,
      },
        new go.Binding('stroke', 'hasError', (err: boolean) =>
          err ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.agent.stroke
        ),
        new go.Binding('strokeWidth', 'hasError', (err: boolean) =>
          err ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.agent.strokeWidth
        )
      ),
      // Logo/icon image (centered inside the circle)
      $(go.Picture, {
        desiredSize: NODE_COMMON_STYLES.iconSize,
      }, new go.Binding('source', 'source')),
      // Error badge (shown only when hasError is true)
      $(go.Picture, {
        desiredSize: NODE_COMMON_STYLES.error.badgeSize,
        alignment: NODE_COMMON_STYLES.error.badgeAlignment,
        visible: false,
        source: '',
      },
        new go.Binding('visible', 'hasError'),
        new go.Binding('source', 'errorBadge')
      ),
      // Left port (transparent)
      $(go.Shape, 'Circle', {
        width: 2,
        height: 2,
        fill: 'transparent',
        stroke: 'transparent',
        portId: 'left',
        alignment: go.Spot.Left,
        fromLinkable: true,
        toLinkable: true,
      }),
      // Right port (transparent)
      $(go.Shape, 'Circle', {
        width: 2,
        height: 2,
        fill: 'transparent',
        stroke: 'transparent',
        portId: 'right',
        alignment: go.Spot.Right,
        fromLinkable: true,
        toLinkable: true,
      })
    ),
    // Label below the circle
    $(
      go.TextBlock,
      {
        font: NODE_COMMON_STYLES.labelFont,
        stroke: NODE_STYLES.agent.textStroke,
        textAlign: 'center',
        margin: NODE_COMMON_STYLES.labelMargin,
        shadowVisible: false,
      },
      new go.Binding('text', 'name')
    )
  );
}
